import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui-elements';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import { useLocation, useParams } from 'wouter';
import { Plus, Trash2, ChevronDown, ChevronLeft, ChevronRight, Store, Filter, Zap, Eye, Loader2, LayoutTemplate, X, SendHorizontal, Phone, CheckCircle2, UserCircle2, Calendar, CalendarCheck } from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

// ─── Types ──────────────────────────────────────────────────────────────────

type ConditionField = 'phone' | 'tag' | 'email' | 'created_at' | 'abandoned_checkout' | 'sms_sent' | 'fulfillment_delay' | 'unfulfilled_days' | 'order_vendor';

interface PhoneCondition { field: 'phone'; operator: 'is_present' | 'is_empty' }
interface TagCondition { field: 'tag'; operator: 'includes' | 'not_includes'; value: string }
interface EmailCondition { field: 'email'; operator: 'contains'; value: string }
interface DateCondition { field: 'created_at'; operator: 'before' | 'after' | 'last_14_days' | 'last_30_days' | 'last_90_days'; value?: string }
interface AbandonedCheckoutCondition { field: 'abandoned_checkout'; operator: 'has_abandoned' | 'has_not_abandoned' }
interface SmsSentCondition { field: 'sms_sent'; operator: 'not_within_7_days' | 'not_within_14_days' | 'not_within_30_days' }
interface FulfillmentDelayCondition { field: 'fulfillment_delay'; operator: 'more_than_days'; value: string }
interface UnfulfilledDaysCondition { field: 'unfulfilled_days'; operator: 'more_than_days'; value: string }
interface OrderVendorCondition { field: 'order_vendor'; operator: 'includes' | 'not_includes'; value: string[] }
type Condition = PhoneCondition | TagCondition | EmailCondition | DateCondition | AbandonedCheckoutCondition | SmsSentCondition | FulfillmentDelayCondition | UnfulfilledDaysCondition | OrderVendorCondition;

interface AddTagAction { type: 'add_tag'; value: string }
interface RemoveTagAction { type: 'remove_tag'; value: string }
interface SendSmsAction { type: 'send_sms'; integrationId: string; messageBody: string }
type AutomationAction = AddTagAction | RemoveTagAction | SendSmsAction;

interface ShopifyStore { id: string; nickname: string; storeUrl: string }
interface MaskedIntegration { id: string; label: string; provider: string }

// ─── Field config ────────────────────────────────────────────────────────────

const DATE_PRESET_OPERATORS = ['last_14_days', 'last_30_days', 'last_90_days'];

const FIELD_CONFIG: Record<ConditionField, { label: string; operators: { value: string; label: string }[]; valueType: 'none' | 'text' | 'date' | 'number' | 'vendor_multiselect'; valuePlaceholder?: string }> = {
  phone:              { label: 'Phone',               operators: [{ value: 'is_present', label: 'is present' }, { value: 'is_empty', label: 'is empty' }],                                                                                                                                   valueType: 'none' },
  tag:                { label: 'Tag',                 operators: [{ value: 'includes', label: 'includes' }, { value: 'not_includes', label: 'does not include' }],                                                                                                                           valueType: 'text' },
  email:              { label: 'Email',               operators: [{ value: 'contains', label: 'contains' }],                                                                                                                                                                                  valueType: 'text' },
  created_at:         { label: 'Customer added date', operators: [{ value: 'last_14_days', label: 'last 14 days' }, { value: 'last_30_days', label: 'last 30 days' }, { value: 'last_90_days', label: 'last 90 days' }, { value: 'after', label: 'after (date)' }, { value: 'before', label: 'before (date)' }], valueType: 'date' },
  abandoned_checkout: { label: 'Abandoned checkout',  operators: [{ value: 'has_abandoned', label: 'has abandoned checkout' }, { value: 'has_not_abandoned', label: 'has NOT abandoned checkout' }],                                                                                        valueType: 'none' },
  sms_sent:           { label: 'SMS send history',    operators: [{ value: 'not_within_7_days', label: 'not sent in last 7 days' }, { value: 'not_within_14_days', label: 'not sent in last 14 days' }, { value: 'not_within_30_days', label: 'not sent in last 30 days' }],               valueType: 'none' },
  fulfillment_delay:  { label: 'Fulfillment delay',   operators: [{ value: 'more_than_days', label: 'fulfilled more than X days after order placed' }],                                                                                                                                      valueType: 'number', valuePlaceholder: 'Days (e.g. 3)' },
  unfulfilled_days:   { label: 'Unfulfilled for',     operators: [{ value: 'more_than_days', label: 'order unfulfilled for more than X days' }],                                                                                                                                              valueType: 'number', valuePlaceholder: 'Days (e.g. 3)' },
  order_vendor:       { label: 'Order vendor',         operators: [{ value: 'includes', label: 'order includes vendor(s)' }, { value: 'not_includes', label: 'order does NOT include vendor(s)' }],                                                                                      valueType: 'vendor_multiselect' },
};

const ACTION_TYPES = [
  { value: 'add_tag',    label: 'Add tag' },
  { value: 'remove_tag', label: 'Remove tag' },
  { value: 'send_sms',   label: 'Send SMS (Podium)' },
];

// ─── Helper components ───────────────────────────────────────────────────────

function Select({ value, onChange, options, placeholder, className = '' }: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={`relative ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none bg-muted/50 border border-border rounded-xl pl-3 pr-9 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 transition-all"
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
    </div>
  );
}

function Input({ value, onChange, placeholder, type = 'text', className = '' }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  className?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 ${className}`}
    />
  );
}

// ─── Vendor multi-select ─────────────────────────────────────────────────────

function VendorMultiSelect({ storeId, token, selected, onChange }: {
  storeId: string;
  token: string;
  selected: string[];
  onChange: (vendors: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [vendors, setVendors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const fetchVendors = async () => {
    if (fetched) return;
    setLoading(true);
    setFetchError(null);
    try {
      const r = await fetch(`${BASE}/api/stores/${storeId}/vendors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${r.status}`);
      }
      const data: string[] = await r.json();
      setVendors(data);
      setFetched(true);
    } catch (err: any) {
      setFetchError(err.message ?? 'Failed to load vendors');
      setFetched(false); // allow retry on next open
    }
    finally { setLoading(false); }
  };

  const handleOpen = () => {
    setOpen(true);
    setSearch('');
    fetchVendors();
  };

  const toggle = (vendor: string) => {
    onChange(selected.includes(vendor) ? selected.filter((v) => v !== vendor) : [...selected, vendor]);
  };

  const remove = (vendor: string) => onChange(selected.filter((v) => v !== vendor));

  const filtered = vendors.filter((v) => !search || v.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="flex-1 min-w-52">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map((v) => (
            <span key={v} className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-primary/15 border border-primary/30 text-primary font-medium">
              {v}
              <button type="button" onClick={() => remove(v)} className="ml-0.5 hover:text-foreground transition-colors">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={handleOpen}
          className="w-full flex items-center justify-between gap-2 bg-muted/50 border border-border rounded-xl pl-3 pr-3 py-2.5 text-sm text-left hover:border-border transition-colors focus:outline-none focus:ring-1 focus:ring-primary/50"
        >
          <span className={selected.length === 0 ? 'text-muted-foreground' : 'text-foreground'}>
            {selected.length === 0 ? 'Select vendors…' : `${selected.length} vendor${selected.length > 1 ? 's' : ''} selected`}
          </span>
          <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </button>

        {open && (
          <div className="absolute top-full mt-1 left-0 z-[9999] w-72 glass-panel rounded-2xl border border-border shadow-2xl overflow-hidden">
            <div className="px-3 pt-3 pb-2">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search vendors..."
                className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-primary/50"
                autoFocus
              />
            </div>
            <div className="max-h-56 overflow-y-auto pb-2">
              {loading && (
                <div className="flex items-center gap-2 justify-center py-6 text-muted-foreground text-xs">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading vendors…
                </div>
              )}
              {!loading && fetchError && (
                <div className="mx-3 mb-2 text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-xl p-3">
                  {fetchError}
                </div>
              )}
              {!loading && !fetchError && filtered.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6">
                  {vendors.length === 0 ? 'No vendors found in this store.' : 'No vendors match your search.'}
                </p>
              )}
              {!loading && filtered.map((v) => {
                const checked = selected.includes(v);
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => toggle(v)}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-muted/50 transition-colors text-left"
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${checked ? 'bg-primary border-primary' : 'border-border bg-muted/50'}`}>
                      {checked && (
                        <svg className="w-2.5 h-2.5 text-black" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1.5 5l2.5 2.5 4.5-4.5" />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm text-foreground truncate">{v}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Condition row ────────────────────────────────────────────────────────────

function ConditionRow({ condition, onChange, onRemove, index, storeId, token }: {
  condition: Condition;
  onChange: (c: Condition) => void;
  onRemove: () => void;
  index: number;
  storeId: string;
  token: string;
}) {
  const cfg = FIELD_CONFIG[condition.field as ConditionField];

  // Guard: unknown / legacy field — show a remove-only row
  if (!cfg) {
    return (
      <div className="flex items-center gap-2">
        {index > 0 && <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded">AND</span>}
        <span className="text-sm text-muted-foreground italic">Unknown condition ({(condition as any).field}) — please remove</span>
        <button onClick={onRemove} className="p-2 text-muted-foreground hover:text-rose-400 hover:bg-rose-400/10 rounded-xl transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const handleFieldChange = (field: string) => {
    const newCfg = FIELD_CONFIG[field as ConditionField];
    const base: any = { field, operator: newCfg.operators[0]!.value };
    if (newCfg.valueType === 'vendor_multiselect') base.value = [];
    else if (newCfg.valueType !== 'none') base.value = '';
    onChange(base as Condition);
  };

  const handleOperatorChange = (operator: string) => {
    onChange({ ...condition, operator } as Condition);
  };

  const handleValueChange = (value: string) => {
    onChange({ ...condition, value } as any);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {index > 0 && <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded">AND</span>}
      <Select
        value={condition.field}
        onChange={handleFieldChange}
        options={Object.entries(FIELD_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
        className="flex-shrink-0 w-36"
      />
      <Select
        value={condition.operator}
        onChange={handleOperatorChange}
        options={cfg.operators}
        className="flex-shrink-0 w-40"
      />
      {cfg.valueType === 'vendor_multiselect' && storeId && (
        <VendorMultiSelect
          storeId={storeId}
          token={token}
          selected={Array.isArray((condition as any).value) ? (condition as any).value : []}
          onChange={(vendors) => onChange({ ...condition, value: vendors } as any)}
        />
      )}
      {cfg.valueType !== 'none' && cfg.valueType !== 'vendor_multiselect' && !DATE_PRESET_OPERATORS.includes(condition.operator) && (
        <Input
          value={(condition as any).value ?? ''}
          onChange={handleValueChange}
          type={cfg.valueType === 'date' ? 'date' : cfg.valueType === 'number' ? 'number' : 'text'}
          placeholder={cfg.valuePlaceholder ?? (cfg.valueType === 'text' ? 'Value...' : undefined)}
          className={cfg.valueType === 'number' ? 'w-32 flex-shrink-0' : 'flex-1 min-w-32'}
        />
      )}
      <button onClick={onRemove} className="p-2 text-muted-foreground hover:text-rose-400 hover:bg-rose-400/10 rounded-xl transition-colors">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Template picker ──────────────────────────────────────────────────────────

interface PodiumTemplate { uid: string; name: string; message: string }

function TemplatePicker({ integrationId, token, onSelect }: {
  integrationId: string;
  token: string;
  onSelect: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<PodiumTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const ref = React.useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${BASE}/api/messaging-integrations/${integrationId}/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? r.statusText);
      }
      const data: PodiumTemplate[] = await r.json();
      setTemplates(data);
    } catch (err: any) {
      setError(err.message ?? 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setSearch('');
    fetchTemplates();
  };

  const filtered = templates.filter((t) =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.message.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={handleOpen}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20 transition-colors"
      >
        <LayoutTemplate className="w-3.5 h-3.5" />
        Pick Template
      </button>

      {open && (
        <div className="absolute top-full mt-2 left-0 z-[9999] w-80 glass-panel rounded-2xl border border-border shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-border">
            <span className="text-xs font-bold text-foreground uppercase tracking-wider">Podium Templates</span>
            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="px-3 pt-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates..."
              className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-primary/50"
              autoFocus
            />
          </div>

          <div className="max-h-72 overflow-y-auto p-2">
            {loading && (
              <div className="flex items-center gap-2 justify-center py-8 text-muted-foreground text-xs">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading templates…
              </div>
            )}
            {!loading && error && (
              <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-xl p-3 m-1">
                {error}
              </div>
            )}
            {!loading && !error && filtered.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-8">
                {templates.length === 0 ? 'No templates found in this Podium location.' : 'No templates match your search.'}
              </p>
            )}
            {!loading && !error && filtered.map((t) => (
              <button
                key={t.uid}
                type="button"
                onClick={() => { onSelect(t.message); setOpen(false); }}
                className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-muted/50 transition-colors group"
              >
                <p className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors truncate">{t.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">{t.message || '(no preview)'}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Test SMS sender ─────────────────────────────────────────────────────────

interface PodiumContact { uid: string; name: string; phone: string; email: string | null; avatarUrl: string | null; }

function TestSmsSender({ integrationId, messageBody, token }: {
  integrationId: string;
  messageBody: string;
  token: string;
}) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<PodiumContact[]>([]);
  const [lookingUp, setLookingUp] = useState(false);
  const [selectedContact, setSelectedContact] = useState<PodiumContact | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Debounced contact lookup
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = phone.trim();
    if (q.length < 3) { setContacts([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLookingUp(true);
      try {
        const r = await fetch(
          `${BASE}/api/messaging-integrations/${integrationId}/contacts?phone=${encodeURIComponent(q)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data: PodiumContact[] = r.ok ? await r.json() : [];
        setContacts(data);
        setShowDropdown(data.length > 0);
      } catch { setContacts([]); setShowDropdown(false); }
      finally { setLookingUp(false); }
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [phone, integrationId, token]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSend = async () => {
    if (!phone.trim() || !messageBody.trim()) return;
    setSending(true);
    setStatus('idle');
    setError(null);
    setShowDropdown(false);
    try {
      const r = await fetch(`${BASE}/api/messaging-integrations/${integrationId}/test-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phone: phone.trim(), messageBody }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error ?? r.statusText);
      setStatus('sent');
      setTimeout(() => { setStatus('idle'); setOpen(false); setSelectedContact(null); setPhone(''); }, 3000);
    } catch (err: any) {
      setStatus('error');
      setError(err.message ?? 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleSelectContact = (c: PodiumContact) => {
    setPhone(c.phone);
    setSelectedContact(c);
    setShowDropdown(false);
    setContacts([]);
  };

  const handleClose = () => {
    setOpen(false);
    setPhone('');
    setSelectedContact(null);
    setContacts([]);
    setShowDropdown(false);
    setStatus('idle');
    setError(null);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setStatus('idle'); setError(null); }}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
      >
        <Phone className="w-3.5 h-3.5" />
        Send Test
      </button>
    );
  }

  return (
    <div className="w-full bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-3 py-2.5 space-y-2">
      <div className="flex items-center gap-2" ref={wrapperRef}>
        <Phone className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
        <div className="relative flex-1 min-w-36">
          <input
            type="tel"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); setSelectedContact(null); setStatus('idle'); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSend(); } if (e.key === 'Escape') setShowDropdown(false); }}
            onFocus={() => contacts.length > 0 && setShowDropdown(true)}
            placeholder="+61 4XX XXX XXX or name…"
            autoFocus
            className="w-full bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-emerald-500/50"
          />
          {lookingUp && (
            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-emerald-400 animate-spin" />
          )}
          {showDropdown && contacts.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-card border border-border rounded-xl shadow-xl overflow-hidden">
              {contacts.map((c) => (
                <button
                  key={c.uid}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); handleSelectContact(c); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    {c.avatarUrl
                      ? <img src={c.avatarUrl} className="w-7 h-7 rounded-full object-cover" alt="" />
                      : <UserCircle2 className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">{c.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{c.phone}{c.email ? ` · ${c.email}` : ''}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {selectedContact && (
          <span className="text-[10px] text-emerald-400 truncate max-w-24 flex-shrink-0">{selectedContact.name}</span>
        )}
        {status === 'sent' ? (
          <span className="flex items-center gap-1 text-xs text-emerald-400 font-medium flex-shrink-0">
            <CheckCircle2 className="w-3.5 h-3.5" /> Sent!
          </span>
        ) : (
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !phone.trim() || !messageBody.trim()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <SendHorizontal className="w-3.5 h-3.5" />}
            {sending ? 'Sending…' : 'Send'}
          </button>
        )}
        <button type="button" onClick={handleClose} className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {status === 'error' && error && (
        <p className="text-xs text-rose-400">{error}</p>
      )}
    </div>
  );
}

// ─── Action row ───────────────────────────────────────────────────────────────

function ActionRow({ action, onChange, onRemove, integrations, token }: {
  action: AutomationAction;
  onChange: (a: AutomationAction) => void;
  onRemove: () => void;
  integrations: MaskedIntegration[];
  token: string;
}) {
  const handleTypeChange = (type: string) => {
    if (type === 'add_tag') onChange({ type: 'add_tag', value: '' });
    else if (type === 'remove_tag') onChange({ type: 'remove_tag', value: '' });
    else onChange({ type: 'send_sms', integrationId: '', messageBody: '' });
  };

  return (
    <div className="flex items-start gap-2 flex-wrap">
      <Select
        value={action.type}
        onChange={handleTypeChange}
        options={ACTION_TYPES}
        className="flex-shrink-0 w-44"
      />
      {(action.type === 'add_tag' || action.type === 'remove_tag') && (
        <Input
          value={action.value}
          onChange={(v) => onChange({ ...action, value: v })}
          placeholder="Tag name..."
          className="flex-1 min-w-32"
        />
      )}
      {action.type === 'send_sms' && (
        <div className="flex flex-col gap-2 flex-1">
          <Select
            value={action.integrationId}
            onChange={(v) => onChange({ ...action, integrationId: v })}
            options={integrations.map((i) => ({ value: i.id, label: i.label }))}
            placeholder="Select messaging account..."
            className="w-full"
          />
          <div className="flex items-start gap-2">
            <textarea
              value={action.messageBody}
              onChange={(e) => onChange({ ...action, messageBody: e.target.value })}
              placeholder="Message body... Use {{first_name}}, {{last_name}}, {{email}}"
              rows={3}
              className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 resize-none"
            />
            {action.integrationId && (
              <div className="flex-shrink-0 pt-1 flex flex-col gap-2">
                <TemplatePicker
                  integrationId={action.integrationId}
                  token={token}
                  onSelect={(msg) => onChange({ ...action, messageBody: msg })}
                />
              </div>
            )}
          </div>
          {action.integrationId && action.messageBody.trim() && (
            <TestSmsSender
              integrationId={action.integrationId}
              messageBody={action.messageBody}
              token={token}
            />
          )}
        </div>
      )}
      <button onClick={onRemove} className="p-2 text-muted-foreground hover:text-rose-400 hover:bg-rose-400/10 rounded-xl transition-colors mt-0.5">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Preview table ────────────────────────────────────────────────────────────

interface PreviewResult {
  totalScanned: number;
  totalMatched: number;
  page: number;
  perPage: number;
  totalPages: number;
  customers: { id: number; name: string; email: string; phone: string | null; tags: string; createdAt: string }[];
}

function PreviewTable({ result, onPageChange, isLoading }: { result: PreviewResult; onPageChange: (p: number) => void; isLoading?: boolean }) {
  return (
    <div className="mt-4">
      <div className="flex gap-4 mb-4">
        <div className="glass-panel rounded-xl p-4 flex-1 text-center">
          <p className="text-2xl font-display font-bold text-primary">{result.totalMatched}</p>
          <p className="text-xs text-muted-foreground mt-1">Matching customers</p>
        </div>
        <div className="glass-panel rounded-xl p-4 flex-1 text-center">
          <p className="text-2xl font-display font-bold">{result.totalScanned}</p>
          <p className="text-xs text-muted-foreground mt-1">Total scanned</p>
        </div>
      </div>
      {result.customers.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">No customers match the current filter conditions.</p>
      ) : (
        <div className="overflow-auto rounded-xl border border-border/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/50 bg-muted/20">
                <th className="text-left px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Phone</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-muted-foreground uppercase tracking-wider">Current Tags</th>
              </tr>
            </thead>
            <tbody>
              {result.customers.map((c) => (
                <tr key={c.id} className="border-b border-border/50 hover:bg-muted/50">
                  <td className="px-4 py-3">{c.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.email}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.phone ?? '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs max-w-48 truncate">{c.tags || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {result.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
              <p className="text-xs text-muted-foreground">Page {result.page} of {result.totalPages} ({result.totalMatched} total)</p>
              <div className="flex items-center gap-2">
                <button
                  disabled={result.page <= 1 || isLoading}
                  onClick={() => onPageChange(result.page - 1)}
                  className="p-1.5 rounded-lg bg-muted/50 border border-border disabled:opacity-40 hover:bg-muted transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-muted-foreground">{result.page} / {result.totalPages}</span>
                <button
                  disabled={result.page >= result.totalPages || isLoading}
                  onClick={() => onPageChange(result.page + 1)}
                  className="p-1.5 rounded-lg bg-muted/50 border border-border disabled:opacity-40 hover:bg-muted transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main builder page ────────────────────────────────────────────────────────

export function CustomerAutomationBuilder() {
  const params = useParams<{ id?: string }>();
  const isEdit = !!params.id && params.id !== 'new';
  const [, setLocation] = useLocation();
  const { token } = useAuthStore();
  const { toast } = useToast();

  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const apiFetch = async (path: string, opts: RequestInit = {}) => {
    const r = await fetch(`${BASE}/api${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', ...authHeader, ...(opts.headers as any) },
    });
    if (!r.ok && r.status !== 204) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.error ?? r.statusText);
    }
    if (r.status === 204) return null;
    return r.json();
  };

  const [stores, setStores] = useState<ShopifyStore[]>([]);
  const [integrations, setIntegrations] = useState<MaskedIntegration[]>([]);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [storeId, setStoreId] = useState('');
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [actions, setActions] = useState<AutomationAction[]>([]);

  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  // Schedule state
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleInterval, setScheduleInterval] = useState('daily');
  const [schedSaving, setSchedSaving] = useState(false);

  const fetchPreviewPage = async (targetId: string, page: number) => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const result = await apiFetch(`/customer-automations/${targetId}/preview?page=${page}&perPage=25`, { method: 'POST' });
      setPreviewResult(result);
    } catch (err: any) {
      setPreviewError(err.message);
    } finally {
      setPreviewing(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const [storeData, integData] = await Promise.all([
          apiFetch('/stores'),
          apiFetch('/messaging-integrations'),
        ]);
        setStores(storeData);
        setIntegrations(integData);

        if (isEdit) {
          const auto = await apiFetch(`/customer-automations/${params.id}`);
          setName(auto.name ?? '');
          setDescription(auto.description ?? '');
          setStoreId(auto.storeId ?? '');
          setConditions(auto.conditions ?? []);
          setActions(auto.actions ?? []);
          setSavedId(auto.id);
          setScheduleEnabled(auto.scheduleEnabled ?? false);
          setScheduleInterval(auto.scheduleInterval ?? 'daily');
        }
      } catch (err: any) {
        toast({ title: 'Error loading data', description: err.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const handleScheduleSave = async () => {
    const targetId = savedId ?? (isEdit ? params.id! : null);
    if (!targetId) return;
    setSchedSaving(true);
    try {
      await apiFetch(`/customer-automations/${targetId}/schedule`, {
        method: 'PATCH',
        body: JSON.stringify({ scheduleEnabled, scheduleInterval }),
      });
      toast({ title: scheduleEnabled ? 'Schedule enabled' : 'Schedule disabled' });
    } catch (err: any) {
      toast({ title: 'Error saving schedule', description: err.message, variant: 'destructive' });
    } finally {
      setSchedSaving(false);
    }
  };

  const addCondition = () => {
    setConditions((prev) => [...prev, { field: 'phone', operator: 'is_present' }]);
  };

  const updateCondition = (i: number, c: Condition) => {
    setConditions((prev) => prev.map((x, idx) => (idx === i ? c : x)));
  };

  const removeCondition = (i: number) => {
    setConditions((prev) => prev.filter((_, idx) => idx !== i));
  };

  const addAction = () => {
    setActions((prev) => [...prev, { type: 'add_tag', value: '' }]);
  };

  const updateAction = (i: number, a: AutomationAction) => {
    setActions((prev) => prev.map((x, idx) => (idx === i ? a : x)));
  };

  const removeAction = (i: number) => {
    setActions((prev) => prev.filter((_, idx) => idx !== i));
  };

  const handleSave = async () => {
    if (!name || !storeId) {
      toast({ title: 'Validation', description: 'Name and store are required.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const body = { name, description, storeId, conditions, actions };
      let result;
      if (isEdit) {
        result = await apiFetch(`/customer-automations/${params.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        result = await apiFetch('/customer-automations', { method: 'POST', body: JSON.stringify(body) });
      }
      setSavedId(result.id);
      toast({ title: isEdit ? 'Automation updated' : 'Automation created' });
      if (!isEdit) {
        setLocation(`/customer-automations/${result.id}/edit`);
      }
    } catch (err: any) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!name || !storeId) {
      toast({ title: 'Fill in required fields', description: 'Name and store are required before previewing.', variant: 'destructive' });
      return;
    }
    // Auto-save to ensure preview reflects current form state
    setSaving(true);
    let resolvedId: string | null = savedId ?? (isEdit ? params.id! : null);
    try {
      const body = { name, description, storeId, conditions, actions };
      let result;
      if (resolvedId) {
        result = await apiFetch(`/customer-automations/${resolvedId}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        result = await apiFetch('/customer-automations', { method: 'POST', body: JSON.stringify(body) });
        setLocation(`/customer-automations/${result.id}/edit`);
      }
      setSavedId(result.id);
      resolvedId = result.id;
    } catch (err: any) {
      toast({ title: 'Could not save before preview', description: err.message, variant: 'destructive' });
      setSaving(false);
      return;
    } finally {
      setSaving(false);
    }
    setPreviewResult(null);
    await fetchPreviewPage(resolvedId!, 1);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const currentSavedId = savedId ?? (isEdit ? params.id : null);

  return (
    <Layout>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-display text-gradient mb-2">{isEdit ? 'Edit Automation' : 'New Automation'}</h1>
          <p className="text-muted-foreground">Define filter conditions and actions to run against your Shopify customers.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setLocation('/customer-automations')}>Cancel</Button>
          <Button variant="secondary" className="gap-2" onClick={handlePreview} isLoading={previewing || saving} disabled={!name || !storeId}>
            <Eye className="w-4 h-4" /> {saving ? 'Saving…' : 'Save & Preview'}
          </Button>
          <Button onClick={handleSave} isLoading={saving} className="gap-2">Save</Button>
        </div>
      </div>

      <div className="space-y-6">

        {/* Store section */}
        <div className="glass-panel rounded-2xl p-6 border border-border">
          <h2 className="text-base font-bold flex items-center gap-2 mb-5 text-foreground">
            <Store className="w-5 h-5 text-primary" /> Store
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1.5">Automation name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Club Member activation"
                className="w-full bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1.5">Description (optional)</label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of what this automation does"
                className="w-full bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
              />
            </div>
            <div>
              {stores.length === 0 ? (
                <p className="text-sm text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-xl px-4 py-3">
                  No Shopify stores connected. Add a store in the Connected Stores section.
                </p>
              ) : (
                <ShopifyTargetStoreSelector
                  stores={stores}
                  value={storeId}
                  onChange={setStoreId}
                  label="Target Shopify store"
                  className="max-w-md"
                />
              )}
            </div>
          </div>
        </div>

        {/* Filter conditions section */}
        <div className="glass-panel rounded-2xl p-6 border border-border relative z-10">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-bold flex items-center gap-2 text-foreground">
              <Filter className="w-5 h-5 text-primary" /> Filter Conditions
              <span className="text-xs font-normal text-muted-foreground ml-1">(all conditions must match — AND logic)</span>
            </h2>
            <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-foreground" onClick={addCondition}>
              <Plus className="w-4 h-4" /> Add Condition
            </Button>
          </div>
          {conditions.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-border rounded-xl">
              <p className="text-sm text-muted-foreground mb-3">No filter conditions — all customers will match</p>
              <Button variant="ghost" size="sm" className="gap-1.5" onClick={addCondition}>
                <Plus className="w-4 h-4" /> Add your first condition
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {conditions.map((condition, i) => (
                <ConditionRow
                  key={i}
                  index={i}
                  condition={condition}
                  onChange={(c) => updateCondition(i, c)}
                  onRemove={() => removeCondition(i)}
                  storeId={storeId}
                  token={token ?? ''}
                />
              ))}
            </div>
          )}
        </div>

        {/* Actions section */}
        <div className="glass-panel rounded-2xl p-6 border border-border">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-bold flex items-center gap-2 text-foreground">
              <Zap className="w-5 h-5 text-primary" /> Actions
              <span className="text-xs font-normal text-muted-foreground ml-1">(executed in order for each matching customer)</span>
            </h2>
            <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-foreground" onClick={addAction}>
              <Plus className="w-4 h-4" /> Add Action
            </Button>
          </div>
          {actions.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-border rounded-xl">
              <p className="text-sm text-muted-foreground mb-3">No actions configured — customers will only be scanned</p>
              <Button variant="ghost" size="sm" className="gap-1.5" onClick={addAction}>
                <Plus className="w-4 h-4" /> Add your first action
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {actions.map((action, i) => (
                <ActionRow
                  key={i}
                  action={action}
                  onChange={(a) => updateAction(i, a)}
                  onRemove={() => removeAction(i)}
                  integrations={integrations}
                  token={token ?? ''}
                />
              ))}
            </div>
          )}
        </div>

        {/* Schedule section — only in edit mode after first save */}
        {currentSavedId && (
          <div className="glass-panel rounded-2xl p-6 border border-border">
            <h2 className="text-base font-bold flex items-center gap-2 mb-5 text-foreground">
              {scheduleEnabled ? <CalendarCheck className="w-5 h-5 text-violet-400" /> : <Calendar className="w-5 h-5 text-primary" />}
              Auto-run Schedule
              {scheduleEnabled && (
                <span className="text-xs font-normal bg-violet-500/15 border border-violet-500/25 text-violet-300 px-2 py-0.5 rounded-lg ml-1">
                  Active
                </span>
              )}
            </h2>
            <div className="flex flex-col sm:flex-row sm:items-end gap-5">
              <div className="space-y-3 flex-1">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setScheduleEnabled((e) => !e)}
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${scheduleEnabled ? 'bg-violet-500' : 'bg-muted'}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${scheduleEnabled ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                  </button>
                  <span className="text-sm text-foreground">
                    {scheduleEnabled ? 'Enabled — will run automatically' : 'Disabled — run manually only'}
                  </span>
                </div>

                {scheduleEnabled && (
                  <div className="ml-12">
                    <label className="block text-xs text-muted-foreground mb-1.5">Run frequency</label>
                    <div className="relative max-w-xs">
                      <select
                        value={scheduleInterval}
                        onChange={(e) => setScheduleInterval(e.target.value)}
                        className="w-full appearance-none bg-muted/50 border border-border rounded-xl pl-3 pr-9 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-violet-500/50 transition-all"
                      >
                        {[
                          { value: 'hourly',    label: 'Every hour' },
                          { value: 'every_6h', label: 'Every 6 hours' },
                          { value: 'every_12h',label: 'Every 12 hours' },
                          { value: 'daily',    label: 'Daily' },
                          { value: 'weekly',   label: 'Weekly' },
                        ].map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      The automation will scan and act on matching customers at this frequency.
                    </p>
                  </div>
                )}
              </div>

              <Button
                onClick={handleScheduleSave}
                isLoading={schedSaving}
                className="shrink-0 gap-2"
                variant={scheduleEnabled ? 'default' : 'secondary'}
              >
                <CalendarCheck className="w-4 h-4" />
                {scheduleEnabled ? 'Enable Schedule' : 'Save Schedule'}
              </Button>
            </div>
          </div>
        )}

        {/* Preview section */}
        {(previewResult || previewError || previewing) && (
          <div className="glass-panel rounded-2xl p-6 border border-border">
            <h2 className="text-base font-bold flex items-center gap-2 mb-4 text-foreground">
              <Eye className="w-5 h-5 text-primary" /> Preview Results
            </h2>
            {previewing && (
              <div className="flex items-center gap-3 py-8 justify-center text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                Scanning customers (this may take a moment)...
              </div>
            )}
            {previewError && (
              <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4 text-sm text-rose-400">
                {previewError}
              </div>
            )}
            {previewResult && (
              <PreviewTable
                result={previewResult}
                isLoading={previewing}
                onPageChange={(p) => {
                  const targetId = savedId ?? (isEdit ? params.id : null);
                  if (targetId) fetchPreviewPage(targetId, p);
                }}
              />
            )}
          </div>
        )}

        {/* Bottom actions */}
        <div className="flex justify-between items-center pt-2">
          <Button variant="ghost" onClick={() => setLocation('/customer-automations')}>
            ← Back to list
          </Button>
          <div className="flex gap-2">
            {currentSavedId && (
              <>
                <Button variant="secondary" className="gap-2" onClick={handlePreview} isLoading={previewing || saving} disabled={!name || !storeId}>
                  <Eye className="w-4 h-4" /> {saving ? 'Saving…' : 'Save & Preview'}
                </Button>
                <Button variant="secondary" className="gap-2" onClick={() => setLocation(`/customer-automations/${currentSavedId}`)}>
                  <Zap className="w-4 h-4" /> Run Now
                </Button>
              </>
            )}
            <Button onClick={handleSave} isLoading={saving}>Save Automation</Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
